import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Activity, BellRing, Boxes, ChevronDown, Database, Download, FlaskConical, Gauge, LayoutGrid, Lock, LogOut, Mail, Package, Percent, RefreshCw, Send, ShieldCheck, ShoppingCart, Smartphone, Sparkles, Ticket, LineChart as LineIcon } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import AdminResearch from "@/components/AdminResearch";
import AdminBatches from "@/components/AdminBatches";
import { useCatalog } from "@/context/CatalogContext";
import { money } from "@/data/products";
import { monthRevenue, playChime, promoLabel, todayRevenue } from "@/components/admin/shared";
import { useAdminActions, authHeader } from "@/components/admin/useAdminActions";
import HomeTab from "@/components/admin/HomeTab";
import OverviewTab from "@/components/admin/OverviewTab";
import SignupsTab from "@/components/admin/SignupsTab";
import OrdersTab from "@/components/admin/OrdersTab";
import InventoryTab from "@/components/admin/InventoryTab";
import CartsTab from "@/components/admin/CartsTab";
import RestockTab from "@/components/admin/RestockTab";
import PromosTab from "@/components/admin/PromosTab";
import GrowthTab from "@/components/admin/GrowthTab";
import AmbassadorsTab from "@/components/admin/AmbassadorsTab";
import TrafficTab from "@/components/admin/TrafficTab";
import CommandTab from "@/components/admin/CommandTab";
import SystemTab from "@/components/admin/SystemTab";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SEEN_KEY = "mx_admin_seen_orders";

export default function Admin() {
  const { products: PRODUCTS, refresh: refreshCatalog } = useCatalog();
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [tab, setTab] = useState("home");
  const [subs, setSubs] = useState([]);
  const [orders, setOrders] = useState([]);
  const [restock, setRestock] = useState([]);
  const [compoundReqs, setCompoundReqs] = useState([]);
  const [carts, setCarts] = useState([]);
  const [stock, setStock] = useState({});
  const [analytics, setAnalytics] = useState(null);
  const [rangeMode, setRangeMode] = useState("7"); // '7' | '30' | 'all'
  const [qtyDraft, setQtyDraft] = useState({});
  const [trackDraft, setTrackDraft] = useState({});
  const [digestHour, setDigestHour] = useState(8);
  const [backups, setBackups] = useState([]);
  const [lastRun, setLastRun] = useState({});
  const [seenOrders, setSeenOrders] = useState(() => parseInt(localStorage.getItem(SEEN_KEY) || "0", 10));
  const [salesPreset, setSalesPreset] = useState("all");
  const [salesStart, setSalesStart] = useState("");
  const [salesEnd, setSalesEnd] = useState("");
  const [acctFmt, setAcctFmt] = useState(false);
  const [installPrompt, setInstallPrompt] = useState(null);
  const [pushKey, setPushKey] = useState("");
  const [pushOn, setPushOn] = useState(false);
  const [monthlyGoal, setMonthlyGoal] = useState(0);
  const [autoWinback, setAutoWinback] = useState(false);
  const [goalDraft, setGoalDraft] = useState("");
  const [traffic, setTraffic] = useState(null);
  const [attest, setAttest] = useState(null);
  const [costs, setCosts] = useState({});
  const [costDraft, setCostDraft] = useState({});
  const [promos, setPromos] = useState([]);
  const [newPromo, setNewPromo] = useState({ code: "", kind: "percent", percent: "", amount_off: "", bundle_qty: "3", bundle_price: "", max_uses: "", expires_at: "" });
  const [dealDraft, setDealDraft] = useState({ code: "", headline: "", until: "" });
  const [newProd, setNewProd] = useState({ name: "", size: "", price: "", blurb: "", image: "white", bestseller: false, cas: "", halfLife: "", sequence: "" });
  const [showNewProd, setShowNewProd] = useState(false);
  const [editProd, setEditProd] = useState(null);
  const [prodDraft, setProdDraft] = useState({});
  const [mktKind, setMktKind] = useState("social");
  const [mktTopic, setMktTopic] = useState("");
  const [mktOut, setMktOut] = useState("");
  const [mktBusy, setMktBusy] = useState(false);
  const [winEmail, setWinEmail] = useState("");
  const [winOut, setWinOut] = useState(null);
  const [winAllBusy, setWinAllBusy] = useState(false);
  const [winAllOut, setWinAllOut] = useState(null);
  const [winBusy, setWinBusy] = useState(false);
  const [blast, setBlast] = useState({ code: "", percent: "", headline: "", message: "", scheduledAt: "" });
  const [blastSchedule, setBlastSchedule] = useState(false);
  const [blastBusy, setBlastBusy] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [ambassadors, setAmbassadors] = useState([]);
  const [ambForm, setAmbForm] = useState({}); // per-id {code, buyer_discount, commission_rate}
  const [resetConfirm, setResetConfirm] = useState("");
  const lowSeen = useRef(null);
  const ambSeen = useRef(null);
  const [loading, setLoading] = useState(false);

  const logout = useCallback(() => {
    axios.post(`${API}/auth/logout`, {}, { withCredentials: true }).catch((err) => console.warn("logout failed", err));
    setAuthed(false);
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [s, o, r, c, ps, an, se, bk, tf, at, cs, pc, cr] = await Promise.all([
        axios.get(`${API}/subscribers`, authHeader()),
        axios.get(`${API}/orders`, authHeader()),
        axios.get(`${API}/restock-requests`, authHeader()),
        axios.get(`${API}/carts`, authHeader()),
        axios.get(`${API}/product-status`),
        axios.get(`${API}/analytics`, authHeader()),
        axios.get(`${API}/settings`, authHeader()),
        axios.get(`${API}/backups`, authHeader()),
        axios.get(`${API}/analytics/traffic`, authHeader()),
        axios.get(`${API}/attestations`, authHeader()),
        axios.get(`${API}/product-costs`, authHeader()),
        axios.get(`${API}/promo-codes`, authHeader()),
        axios.get(`${API}/compound-requests`, authHeader()),
      ]);
      setSubs(s.data); setOrders(o.data); setRestock(r.data); setCarts(c.data); setStock(ps.data || {}); setAnalytics(an.data);
      setCompoundReqs(cr.data || []);
      if (se.data?.digest_hour !== undefined) setDigestHour(se.data.digest_hour);
      setLastRun({ digest: se.data?.last_digest_at, backup: se.data?.last_backup_at });
      setMonthlyGoal(se.data?.monthly_goal || 0);
      setAutoWinback(!!se.data?.auto_winback_enabled);
      setGoalDraft(se.data?.monthly_goal ? String(se.data.monthly_goal) : "");
      setBackups(bk.data?.backups || []);
      setTraffic(tf.data);
      setAttest(at.data);
      setCosts(cs.data || {});
      setPromos(pc.data?.codes || []);
      try {
        const cp = await axios.get(`${API}/campaigns`, authHeader());
        setCampaigns(cp.data?.campaigns || []);
      } catch { /* no campaigns yet */ }
      try {
        const am = await axios.get(`${API}/ambassadors`, authHeader());
        const list = am.data?.ambassadors || [];
        setAmbassadors(list);
        if (ambSeen.current === null) ambSeen.current = list.length; // baseline for live new-application alerts
      } catch { /* none yet */ }
      try {
        const dl = await axios.get(`${API}/deal`);
        if (dl.data?.active) setDealDraft({ code: dl.data.code, headline: dl.data.headline || "", until: dl.data.until ? dl.data.until.slice(0, 16) : "" });
      } catch { /* no deal set */ }
    } catch { toast.error("Session expired. Please log in again."); logout(); }
    finally { setLoading(false); }
  }, [logout]);

  useEffect(() => {
    axios.get(`${API}/auth/me`, { withCredentials: true })
      .then(() => setAuthed(true))
      .catch(() => { /* not signed in yet */ })
      .finally(() => setChecking(false));
  }, []);

  useEffect(() => { if (authed) loadAll(); }, [authed, loadAll]);

  useEffect(() => {
    if (!authed) return;
    const days = rangeMode === "30" || rangeMode === "all" ? 30 : 7;
    axios.get(`${API}/analytics?days=${days}`, authHeader())
      .then(({ data }) => setAnalytics(data)).catch((err) => console.warn("analytics refresh failed", err));
  }, [rangeMode, authed]);

  // Capture the browser's install prompt so we can offer "Add to phone".
  useEffect(() => {
    const handler = (e) => { e.preventDefault(); setInstallPrompt(e); };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  // Seed the "seen" baseline once, then poll for new orders every 30s (chime + badge).
  useEffect(() => {
    if (!authed) return;
    if (!localStorage.getItem(SEEN_KEY) && orders.length > 0) {
      localStorage.setItem(SEEN_KEY, String(orders.length));
      setSeenOrders(orders.length);
    }
  }, [authed, orders.length]);

  useEffect(() => {
    if (!authed) return;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/orders`, authHeader());
        setOrders((prev) => {
          if (data.length > prev.length) playChime();
          return data;
        });
      } catch { /* ignore transient poll errors */ }
      try {
        const { data: amb } = await axios.get(`${API}/ambassadors`, authHeader());
        const list = amb?.ambassadors || [];
        setAmbassadors(list);
        if (ambSeen.current === null) {
          ambSeen.current = list.length; // seed baseline on first poll
        } else if (list.length > ambSeen.current) {
          const delta = list.length - ambSeen.current;
          const latest = list.filter((a) => a.status === "pending")
            .sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""))[0];
          ambSeen.current = list.length;
          playChime();
          toast.info(`New ambassador application${delta > 1 ? `s (${delta})` : ""}${latest ? ` — ${latest.name}` : ""}`, { duration: 9000 });
        }
      } catch { /* ignore transient poll errors */ }
    };
    const iv = setInterval(poll, 30000);
    return () => clearInterval(iv);
  }, [authed]);

  // Clear the new-order badge when the Orders tab is opened.
  useEffect(() => {
    if (tab === "orders" && orders.length !== seenOrders) {
      localStorage.setItem(SEEN_KEY, String(orders.length));
      setSeenOrders(orders.length);
    }
  }, [tab, orders.length, seenOrders]);

  // Load push key + current subscription state.
  useEffect(() => {
    if (!authed) return;
    axios.get(`${API}/push/vapid-public`).then(({ data }) => setPushKey(data?.enabled ? data.public_key : "")).catch(() => {});
    if ("serviceWorker" in navigator && "PushManager" in window) {
      navigator.serviceWorker.ready.then((reg) => reg.pushManager.getSubscription())
        .then((sub) => setPushOn(!!sub)).catch(() => {});
    }
  }, [authed]);

  // In-app ping the moment a compound newly drops into low stock.
  useEffect(() => {
    const ids = (analytics?.low_stock || []).map((l) => l.product_id);
    if (lowSeen.current === null) { lowSeen.current = new Set(ids); return; }
    const fresh = ids.filter((id) => !lowSeen.current.has(id));
    fresh.forEach((id) => {
      const prod = PRODUCTS.find((p) => p.id === id);
      const q = analytics.low_stock.find((l) => l.product_id === id)?.quantity;
      toast.warning(`Low stock: ${prod ? prod.name : id} — ${q} left`);
    });
    lowSeen.current = new Set(ids);
  }, [analytics, PRODUCTS]);

  const newOrders = Math.max(0, orders.length - seenOrders);
  const mRevenue = monthRevenue(orders);
  const tRevenue = todayRevenue(orders);
  const pendingOrders = orders.filter((o) => o.status === "pending").length;
  const goalPct = monthlyGoal > 0 ? Math.min(100, Math.round((mRevenue / monthlyGoal) * 100)) : 0;
  const inv = PRODUCTS.reduce((acc, p) => {
    const st = stock[p.id];
    const q = st && st.quantity != null ? st.quantity : null;
    const soldOut = st ? st.sold_out : !!p.soldOut;
    const c = costs[p.id];
    if (q != null && !soldOut) {
      acc.value += p.price * q; acc.units += q; acc.tracked += 1;
      if (c != null && !isNaN(c)) { acc.costValue += c * q; acc.retailCosted += p.price * q; acc.costedSkus += 1; }
    } else if (!soldOut) { acc.untracked += 1; }
    return acc;
  }, { value: 0, units: 0, tracked: 0, untracked: 0, costValue: 0, retailCosted: 0, costedSkus: 0 });
  const invMargin = inv.retailCosted - inv.costValue;
  const invMarginPct = inv.retailCosted > 0 ? Math.round((invMargin / inv.retailCosted) * 100) : 0;

  const [payingId, setPayingId] = useState(null);
  const [openPayouts, setOpenPayouts] = useState({});
  const actions = useAdminActions({
    loadAll, refreshCatalog, PRODUCTS, stock, promos, subs, ambForm, blast, blastSchedule,
    qtyDraft, costDraft, trackDraft, prodDraft, newProd, newPromo, dealDraft,
    winEmail, resetConfirm, goalDraft, mktKind, mktTopic, autoWinback, installPrompt, pushKey,
    email, password,
    setInstallPrompt, setPushOn, setMonthlyGoal, setResetConfirm, setAuthed, setOrders,
    setStock, setCosts, setNewProd, setShowNewProd, setEditProd, setProdDraft, setMktOut,
    setMktBusy, setWinAllBusy, setWinAllOut, setWinBusy, setWinOut, setAutoWinback,
    setBlastBusy, setBlast, setBlastSchedule, setNewPromo, setDealDraft, setCarts,
    setDigestHour, setPayingId,
  });
  const { login, installApp, exportCsv } = actions;

  if (checking) return <main className="pt-40 text-center font-mono text-sm text-[#52525B]" data-testid="admin-loading">Checking session…</main>;

  if (!authed) {
    return (
      <main className="pt-40 pb-24 min-h-screen" data-testid="admin-login">
        <div className="max-w-sm mx-auto px-6 text-center">
          <Lock className="mx-auto mb-6" size={30} color="#D4AF37" strokeWidth={1.3} />
          <h1 className="font-serif text-4xl font-light mb-8">Admin Login</h1>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" data-testid="admin-email-input"
            className="w-full bg-transparent border-b border-zinc-600 focus:border-[#D4AF37] outline-none py-3 text-center font-mono text-sm placeholder:text-[#52525B] mb-6 transition-colors" />
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} onKeyDown={(e) => e.key === "Enter" && login()}
            placeholder="Password" data-testid="admin-password-input"
            className="w-full bg-transparent border-b border-zinc-600 focus:border-[#D4AF37] outline-none py-3 text-center font-mono text-sm placeholder:text-[#52525B] transition-colors" />
          <button onClick={login} data-testid="admin-login-btn"
            className="mt-8 w-full bg-[#D4AF37] text-black py-4 font-mono text-xs uppercase tracking-[0.2em] hover:bg-[#C5A028] transition-colors">Sign In</button>
          <button data-testid="admin-forgot-btn"
            onClick={async () => {
              if (!email.trim()) { toast.error("Enter your admin email above first"); return; }
              try {
                await axios.post(`${API}/auth/forgot-password`, { email: email.trim() });
                toast.success("If that email matches the admin account, a reset link is on its way.", { duration: 8000 });
              } catch { toast.error("Could not send reset email — try again shortly"); }
            }}
            className="mt-5 font-mono text-[10px] uppercase tracking-[0.2em] text-[#52525B] hover:text-[#D4AF37] transition-colors">
            Forgot password?
          </button>
        </div>
      </main>
    );
  }

  const stat = (n, l) => (
    <div className="border border-zinc-800 bg-[#0C0C0C] p-6">
      <p className="font-serif text-5xl">{n}</p>
      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#52525B] mt-2">{l}</p>
    </div>
  );

  const revenue = orders.filter((o) => o.status !== "cancelled").reduce((s, o) => s + (o.total || 0), 0);
  const soldOutCount = PRODUCTS.filter((p) => (p.id in stock ? stock[p.id].sold_out : p.soldOut)).length;

  const grouped = Object.values(restock.reduce((acc, r) => {
    const k = r.product_id;
    acc[k] = acc[k] || { product_id: r.product_id, product_name: r.product_name, pending: 0, total: 0 };
    acc[k].total += 1; if (!r.notified) acc[k].pending += 1;
    return acc;
  }, {}));
  const pendingRestock = restock.filter((r) => !r.notified).length;
  const pendingAmbassadors = ambassadors.filter((a) => a.status === "pending").length;
  const waitingByProduct = restock.reduce((acc, r) => {
    if (!r.notified) acc[r.product_id] = (acc[r.product_id] || 0) + 1;
    return acc;
  }, {});

  const tabBtn = (id, label, Icon, badge = 0) => (
    <button onClick={() => setTab(id)} data-testid={`tab-${id}`}
      className={`relative flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.15em] px-5 py-2.5 border transition-colors ${tab === id ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-zinc-800 text-[#A1A1AA] hover:border-[#D4AF37]/50"}`}>
      {Icon && <Icon size={13} />}{label}
      {badge > 0 && (
        <span className="absolute -top-2 -right-2 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-red-500 text-white text-[9px] font-bold" data-testid={`tab-badge-${id}`}>{badge}</span>
      )}</button>
  );

  const ctx = { ...actions, PRODUCTS, acctFmt, ambForm, ambassadors, analytics, attest, autoWinback, backups, blast, blastBusy, blastSchedule, campaigns, carts, compoundReqs, costDraft, costs, dealDraft, digestHour, editProd, email, goalDraft, goalPct, grouped, inv, invMargin, invMarginPct, lastRun, mRevenue, mktBusy, mktKind, mktOut, mktTopic, monthlyGoal, newOrders, newProd, newPromo, openPayouts, orders, payingId, pendingAmbassadors, pendingOrders, pendingRestock, prodDraft, promos, pushOn, qtyDraft, rangeMode, resetConfirm, restock, revenue, salesEnd, salesPreset, salesStart, setAcctFmt, setAmbForm, setBlast, setBlastSchedule, setCostDraft, setDealDraft, setEditProd, setGoalDraft, setMktKind, setMktTopic, setNewProd, setNewPromo, setOpenPayouts, setProdDraft, setQtyDraft, setRangeMode, setResetConfirm, setSalesEnd, setSalesPreset, setSalesStart, setShowNewProd, setTab, setTrackDraft, setWinEmail, showNewProd, soldOutCount, stock, subs, tRevenue, tab, traffic, waitingByProduct, winAllBusy, winAllOut, winBusy, winEmail, winOut };

  return (
    <main className="pt-28 pb-24 min-h-screen" data-testid="admin-dashboard">
      <div className="max-w-5xl mx-auto px-6 md:px-12">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-10">
          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#D4AF37] mb-4">Dashboard</p>
            <h1 className="font-serif text-5xl md:text-6xl font-light leading-none">Molecularix</h1>
          </div>
          <div className="flex gap-3">
            <button onClick={installApp} data-testid="admin-install-btn" className="flex items-center gap-2 border border-[#D4AF37]/50 text-[#D4AF37] px-5 py-3 font-mono text-[10px] uppercase tracking-[0.15em] hover:bg-[#D4AF37] hover:text-black transition-colors">
              <Smartphone size={13} /> Install
            </button>
            <button onClick={loadAll} data-testid="admin-refresh-btn" className="flex items-center gap-2 border border-zinc-700 px-5 py-3 font-mono text-[10px] uppercase tracking-[0.15em] hover:border-[#D4AF37] transition-colors">
              <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
            <button onClick={logout} data-testid="admin-logout-btn" className="flex items-center gap-2 border border-zinc-700 px-5 py-3 font-mono text-[10px] uppercase tracking-[0.15em] hover:border-red-500 hover:text-red-400 transition-colors">
              <LogOut size={13} /> Logout
            </button>
          </div>
        </div>

        {/* Unified sticky 5-group navigation with dropdown sub-sections */}
        <div className="sticky top-14 md:top-16 z-30 py-2 max-w-full overflow-hidden bg-[#0A0A0C]/95 backdrop-blur border-b border-[#1E1E24] mb-6" data-testid="admin-group-nav">
          <div className="grid grid-cols-[repeat(5,minmax(0,1fr))] gap-1">
            {[
              { id: "g-overview", label: "Overview", Icon: LayoutGrid, first: "home", badge: 0 },
              { id: "g-orders", label: "Orders", Icon: Package, first: "orders", badge: newOrders },
              { id: "g-inventory", label: "Inventory", Icon: Boxes, first: "inventory", badge: pendingRestock },
              { id: "g-amb", label: "Elite", Icon: Sparkles, first: "ambassadors", badge: pendingAmbassadors },
              { id: "g-system", label: "System", Icon: ShieldCheck, first: "system", badge: 0 },
            ].map((g) => {
              const subs = {
                "g-overview": [["home", "Home"], ["command", "Command"], ["overview", "Analytics"], ["traffic", "Traffic"], ["growth", "Growth"]],
                "g-orders": [["orders", "Orders"], ["carts", "Carts"], ["promos", "Promos"]],
                "g-inventory": [["inventory", "Stock"], ["restock", "Restock"], ["research", "Research"], ["batches", "Batches"]],
                "g-amb": [["ambassadors", "Ambassadors"]],
                "g-system": [["system", "System"], ["signups", "Signups"]],
              }[g.id];
              const active = subs.some(([id]) => id === tab);
              const btnCls = `relative w-full min-w-0 flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-2 py-2.5 px-0.5 font-mono text-[8px] sm:text-[10px] uppercase tracking-[0.05em] sm:tracking-[0.1em] border transition-colors ${active ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-[#1E1E24] text-[#A1A1AA] hover:border-[#D4AF37]/50"}`;
              const badge = g.badge > 0 && <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-red-500 text-white text-[9px] font-bold" data-testid={`group-badge-${g.id}`}>{g.badge}</span>;
              if (subs.length < 2) {
                return (
                  <button key={g.id} onClick={() => setTab(g.first)} data-testid={`group-${g.id}`} className={btnCls}>
                    <g.Icon size={14} /><span className="truncate">{g.label}</span>{badge}
                  </button>
                );
              }
              return (
                <DropdownMenu key={g.id}>
                  <DropdownMenuTrigger asChild>
                    <button data-testid={`group-${g.id}`} className={btnCls}>
                      <g.Icon size={14} />
                      <span className="truncate flex items-center gap-0.5">{g.label}<ChevronDown size={9} className="shrink-0 opacity-70" /></span>
                      {badge}
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="center" className="bg-[#0C0C0C] border-[#1E1E24] text-[#A1A1AA] font-mono text-xs min-w-[170px]">
                    {subs.map(([id, label]) => (
                      <DropdownMenuItem key={id} onClick={() => setTab(id)} data-testid={`tab-${id}`}
                        className={`uppercase tracking-[0.12em] text-[11px] cursor-pointer focus:bg-[#D4AF37] focus:text-black ${tab === id ? "text-[#D4AF37]" : ""}`}>
                        {label}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              );
            })}
          </div>
        </div>

        {tab === "research" && (
          <div data-testid="research-panel">
            <div className="mb-6">
              <h2 className="font-serif text-3xl font-light mb-1">Research Library</h2>
              <p className="text-[#71717A] text-sm">Edit compound overviews, chemistry, evidence level, and visibility — no code required. Changes go live immediately.</p>
            </div>
            <AdminResearch />
          </div>
        )}

        {tab === "batches" && (
          <div data-testid="batches-panel">
            <div className="mb-6">
              <h2 className="font-serif text-3xl font-light mb-1">Batch Documentation</h2>
              <p className="text-[#71717A] text-sm">Add batch/lot records and attach real Certificates of Analysis (PDF). These power the public <span className="text-[#D4AF37]">Verify a Batch</span> lookup — never fabricate results.</p>
            </div>
            <AdminBatches />
          </div>
        )}

        {tab === "home" && <HomeTab {...ctx} />}

        {tab === "command" && <CommandTab />}

        {tab === "overview" && <OverviewTab {...ctx} />}

        {tab === "signups" && <SignupsTab {...ctx} />}

        {tab === "orders" && <OrdersTab {...ctx} />}

        {tab === "inventory" && <InventoryTab {...ctx} />}

        {tab === "carts" && <CartsTab {...ctx} />}

        {tab === "restock" && <RestockTab {...ctx} />}

        {tab === "promos" && <PromosTab {...ctx} />}

        {tab === "growth" && <GrowthTab {...ctx} />}

        {tab === "ambassadors" && <AmbassadorsTab {...ctx} />}


        {tab === "traffic" && <TrafficTab {...ctx} />}

        {tab === "system" && <SystemTab {...ctx} />}
      </div>
    </main>
  );
}
